import { Modal, Tag, Tooltip } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import {
    formatCurrency,
    getPolicy,
    getProviderKey,
    isSalePaid,
    PROVIDER_LABELS,
} from "../../lib/insuranceSaleApi";

const formatDate = (value) => (value ? moment(value).format("MM/DD/YYYY h:mm A") : "—");

const FieldLabel = ({ children }) => (
    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{children}</p>
);

FieldLabel.propTypes = {
    children: PropTypes.node.isRequired,
};

const InsuranceSaleDetailsModal = ({
    open,
    sale,
    onClose,
    t,
    getStatusColor,
    getPolicyStatusLabel,
}) => {
    if (!sale) {
        return null;
    }

    const policy = getPolicy(sale);
    const status = policy?.status;
    const normalizedStatus = String(status ?? "").toLowerCase();
    const paid = isSalePaid(sale);
    const provider = getProviderKey(sale);

    const statusTag = (
        <Tag color={getStatusColor(status)} className="!m-0 inline-flex w-fit rounded-full px-3">
            {getPolicyStatusLabel(status, t)}
        </Tag>
    );

    return (
        <Modal
            open={open}
            title={t("table.saleDetailsTitle", { id: sale.id })}
            onCancel={onClose}
            footer={null}
            centered
            destroyOnClose
            width={{
                xs: "calc(100vw - 2rem)",
                sm: 480,
            }}
            styles={{
                body: { maxHeight: "min(80vh, 720px)", overflowY: "auto", paddingTop: 8 },
            }}>
            <div className="space-y-4">
                <div className="border-b border-gray-100 pb-4">
                    <FieldLabel>{t("table.customer")}</FieldLabel>
                    <p className="mt-1 text-base font-semibold text-[#2F2F46]">{sale.customerName || "—"}</p>
                    {sale.email ? <p className="mt-0.5 break-all text-sm text-gray-500">{sale.email}</p> : null}
                </div>

                <div className="border-b border-gray-100 pb-4">
                    <FieldLabel>{t("table.vehicle")}</FieldLabel>
                    <p className="mt-1 text-sm font-medium text-[#2F2F46]">{sale.carName || "—"}</p>
                    {sale.carSelected?.VIN ? (
                        <p className="mt-1 break-all font-mono text-xs text-gray-500">{sale.carSelected.VIN}</p>
                    ) : null}
                </div>

                <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-4">
                    <div>
                        <FieldLabel>{t("table.provider")}</FieldLabel>
                        <p className="mt-1 text-sm font-medium text-[#2F2F46]">
                            {PROVIDER_LABELS[provider] ?? "—"}
                        </p>
                    </div>
                    <div>
                        <FieldLabel>{t("table.soldOn")}</FieldLabel>
                        <p className="mt-1 text-sm font-medium text-[#2F2F46]">
                            {sale.createdAt ? moment(sale.createdAt).format("MM/DD/YYYY") : "—"}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-b border-gray-100 pb-4">
                    <div>
                        <FieldLabel>{t("table.period")}</FieldLabel>
                        <p className="mt-1 text-sm font-medium text-[#2F2F46]">{formatDate(sale.pickupDate)}</p>
                        <p className="text-xs text-gray-500">{formatDate(sale.dropoffDate)}</p>
                    </div>
                    <div>
                        <FieldLabel>{t("table.total")}</FieldLabel>
                        <p className="mt-1 text-sm font-semibold text-[#2F2F46]">
                            {formatCurrency(sale.invoice?.total)}
                        </p>
                    </div>
                </div>

                <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col items-start gap-1">
                        <FieldLabel>{t("table.policyStatus")}</FieldLabel>
                        {policy?.lastError && normalizedStatus === "failed" ? (
                            <Tooltip title={policy.lastError}>
                                <span className="inline-flex w-fit">{statusTag}</span>
                            </Tooltip>
                        ) : (
                            statusTag
                        )}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                        <FieldLabel>{t("table.payment")}</FieldLabel>
                        <Tag color={paid ? "success" : "warning"} className="!m-0 inline-flex w-fit rounded-full px-3">
                            {paid ? t("table.paid") : t("table.unpaid")}
                        </Tag>
                        {!paid ? (
                            <span className="text-xs text-gray-500">
                                {formatCurrency(sale.invoice?.due ?? sale.invoice?.total)}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

InsuranceSaleDetailsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    sale: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    getStatusColor: PropTypes.func.isRequired,
    getPolicyStatusLabel: PropTypes.func.isRequired,
};

InsuranceSaleDetailsModal.defaultProps = {
    sale: null,
};

export default InsuranceSaleDetailsModal;
